import { Component, Input } from '@angular/core';
import { UnitOfWork } from '@data';
import { ProximityAreaEnum, ProximityRight, ProximityRightEnum, ProximityRole, ProximityRoleRightMap } from '@models';
import { EntityState } from 'breeze-client';
import * as _ from 'lodash';

interface RightVm {
  right: ProximityRight;
  hasRight: boolean;
  entity?: ProximityRoleRightMap;
}

const RightCapabilities: Partial<Record<ProximityRightEnum, string[]>> = {
  [ProximityRightEnum.CanManageAccountAdministration]: [
    'Manage account administrator users',
    'Create, edit, and delete account roles',
    'Manage administrator groups and role assignments',
    'Impersonate other account administrators',
    'Configure account notification settings',
  ],
  [ProximityRightEnum.CanManageAccountOrganization]: [
    'Manage account users (create, edit, delete)',
    'Configure shipping groups and addresses',
    'Configure billing addresses',
    'Manage account budgets',
    'Import users from spreadsheets',
    'Manage procurement cards',
    'Manage purchase orders',
    'Configure notification settings and templates',
  ],
  [ProximityRightEnum.CanEditProgram]: [
    "Edit any program's settings and configuration",
    'Configure program product types and allowances',
    'Manage program suppliers and partnerships',
    'Configure program tags, views, and billing',
    'Manage program issuances and rapid templates',
    'Configure program kits and budgets',
  ],
  [ProximityRightEnum.CanCreateProgram]: ['Create new programs for the account'],
  [ProximityRightEnum.CanEditAccountHelp]: ['Edit in-app help content and documentation for account pages'],
  [ProximityRightEnum.IsInAllApprovalTrees]: [
    'View and manage all programs regardless of organization tree assignment',
    'Access all users and orders across every organization tree',
    'Bypasses organization tree filtering restrictions',
  ],
  [ProximityRightEnum.CanEditUserByApprovalTree]: [
    'Edit account users within assigned organization trees',
    'Access user management section',
  ],
  [ProximityRightEnum.CanEditJobOrderByApprovalTree]: [
    'Edit job orders within assigned organization trees',
    'Access and modify job order forms and details',
  ],
  [ProximityRightEnum.CanApproveJobOrderByApprovalTree]: [
    'Approve or reject job orders within assigned organization trees',
    'Access the approval dashboard and pending orders',
  ],
  [ProximityRightEnum.CanImpersonateUsersByApprovalTree]: [
    'Act as other account users within assigned organization trees',
    'Enables admin support and troubleshooting',
  ],
};

/** Handles checkboxes for setting role rights.  Saving must be handled by parent form. */
@Component({
  selector: 'prox-role-rights-frm',
  templateUrl: './role-rights-frm.component.html',
  standalone: false,
})
export class RoleRightsFrmComponent {
  @Input() areaId!: ProximityAreaEnum;
  @Input() allRights!: ProximityRight[];
  @Input() uow!: UnitOfWork;
  @Input() isReadOnly!: boolean;
  @Input() set proximityRole(val: ProximityRole) {
    this._proximityRole = val;
    setTimeout(() => this.initRole(), 0);
  }
  get proximityRole() {
    return this._proximityRole;
  }

  private _proximityRole!: ProximityRole;
  isPageReady = false;
  rights!: ProximityRight[];
  roleRights: RightVm[] = [];
  selectedRight: RightVm | null = null;

  async initRole(): Promise<void> {
    this.isPageReady = false;
    this.selectedRight = null;
    if (!this._proximityRole || !this.allRights || !this.areaId) {
      return;
    }

    const roleRights = this._proximityRole.proximityRoleRightMaps;
    this.rights = this.allRights.filter((r) => r.areaId == this.areaId);
    this.roleRights = this.rights.map((r) => {
      const entity = roleRights.find((u) => u.proximityRightId === r.id);
      return {
        right: r,
        hasRight: !!entity,
        entity,
      };
    });

    this.isPageReady = true;
  }

  // not needed because we only show one area
  // private groupRightsIntoAreas() {
  //   this.areaNames = [];
  //   this.areaRights = {};
  //   const roleRights = this._proximityRole.proximityRoleRightMaps;
  //   this.rights = this.allRights.filter(r => this.areas.includes(r.areaId));
  //   this.areas.forEach(a => {
  //     const ar = this.rights.filter(r => r.areaId == a).map(r => {
  //       const entity = roleRights.find(u => u.proximityRightId === r.id);
  //       return {
  //         right: r,
  //         hasRight: !!entity,
  //         entity
  //       }
  //     });
  //     if (ar.length) {
  //       const areaName = ProximityAreaEnum[a];
  //       this.areaNames.push(areaName);
  //       this.areaRights[areaName] = ar;
  //     }
  //   });
  // }

  selectRight(vm: RightVm) {
    this.selectedRight = this.selectedRight === vm ? null : vm;
  }

  getSelectedCapabilities(): string[] {
    if (!this.selectedRight) return [];
    return RightCapabilities[this.selectedRight.right.id as ProximityRightEnum] || [];
  }

  formatName(name: string) {
    return _.startCase(name);
  }

  isDisabled() {
    return this.isReadOnly;
  }

  toggleRight(vm: RightVm) {
    if (vm.hasRight) {
      if (vm.entity && vm.entity.entityAspect.entityState.isDeleted()) {
        // user previously had right; was removed and re-added
        vm.entity.entityAspect.setEntityState(EntityState.Unchanged);
      } else {
        vm.entity = this.uow.createEntity(ProximityRoleRightMap, { proximityRightId: vm.right.id, proximityRoleId: this.proximityRole.id });
      }
    } else {
      if (vm.entity) {
        vm.entity.entityAspect.setDeleted();
      }
    }
  }
}
